<template>
  <div class="container">
    <div class="row">
      <div class="col">
        <h3 class="text-center">
          <span style="padding: 0 12px;border-left:1px solid;border-right:1px solid;">{{ $t('picture.title') }}</span>
        </h3>
      </div>
    </div>
    <div class="row">
      <div class="col">
        <b-breadcrumb style="background-color: transparent;">
          <b-breadcrumb-item :to="{ name: 'index' }">{{ $t('index.title') }}</b-breadcrumb-item>
          <b-breadcrumb-item :to="{ name: 'pictures' }">{{ $t('picture.title') }}</b-breadcrumb-item>
          <b-breadcrumb-item
            :to="{ name: 'pictures', params: { group: 'applicable' } }"
            v-if="group === 'applicable'"
          >{{ $t('picture.list.application.title') }}</b-breadcrumb-item>
          <b-breadcrumb-item
            :to="{ name: 'pictures', params: { group: 'downloadable' } }"
            v-if="group === 'downloadable'"
          >{{ $t('picture.list.download.title') }}</b-breadcrumb-item>
        </b-breadcrumb>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <b-nav pills fill>
          <b-nav-item
            :to="{ name: 'pictures', params: { group: 'applicable' } }"
            :active="group === 'applicable'"
          >{{ $t('picture.list.application.title') }}</b-nav-item>
          <b-nav-item
            :to="{ name: 'pictures', params: { group: 'downloadable' } }"
            :active="group === 'downloadable'"
          >{{ $t('picture.list.download.title') }}</b-nav-item>
          <b-nav-item :to="{ name: 'pictures-search' }">{{ $t('picture.list.search.title') }}</b-nav-item>
        </b-nav>
      </div>
    </div>
    <div class="row mt-3" v-if="group === 'applicable'">
      <div class="col-12">
        <application-list-button-toolbar></application-list-button-toolbar>
      </div>
    </div>
    <div class="row mt-3" v-if="group == 'applicable'">
      <div class="col-12">
        <b-button
          variant="link"
          target="_blank"
          href="https://www2.budaedu.org/dharma-data/dharmas.xlsx"
        >{{ $t('picture.list.application.link.master-list-download') }}</b-button>
      </div>
    </div>
    <b-overlay :show="is_loading" rounded="sm" opacity="0.6">
      <div class="row mt-3">
        <div class="col-12">
          <b-form-select v-model="per_page" :options="per_page_options"></b-form-select>
        </div>
      </div>
      <picture-card-list
        :pictures="pictures"
        :applicable="group === 'applicable'"
      ></picture-card-list>
      <div class="row">
        <div class="col-12">
          <b-pagination
            size="lg"
            align="center"
            v-model="page"
            :total-rows="total"
            :per-page="per_page"
            @change="pageChange"
          ></b-pagination>
        </div>
      </div>
      <template #overlay>
        <div class="text-center">
          <b-icon icon="arrow-clockwise" font-scale="5" animation="spin-pulse"></b-icon>
        </div>
      </template>
    </b-overlay>
  </div>
</template>
<script>
import Vue from 'vue'

import { BreadcrumbPlugin } from 'bootstrap-vue'
import { ButtonGroupPlugin } from 'bootstrap-vue'
import { ButtonPlugin } from 'bootstrap-vue'
import { ButtonToolbarPlugin } from 'bootstrap-vue'
import { FormSelectPlugin } from 'bootstrap-vue'
import { IconsPlugin } from 'bootstrap-vue'
import { NavPlugin } from 'bootstrap-vue'
import { OverlayPlugin } from 'bootstrap-vue'
import { PaginationPlugin } from 'bootstrap-vue'

import ApplicationListButtonToolbar from '@/components/button-toolbars/application-list-button-toolbar/ApplicationListButtonToolbar'
import PictureCardList from '@/components/lists/PictureCardList'
import PictureEfileList from '@/components/lists/PictureEfileList'
import PictureInfo from '@/components/info/PictureInfo'

import i18n from '@/plugins/i18n.js'

import PictureApiService from '@/services/budaedu-api-services/picture-api-service.js'

Vue.use(BreadcrumbPlugin)
Vue.use(ButtonGroupPlugin)
Vue.use(ButtonPlugin)
Vue.use(ButtonToolbarPlugin)
Vue.use(FormSelectPlugin)
Vue.use(IconsPlugin)
Vue.use(NavPlugin)
Vue.use(OverlayPlugin)
Vue.use(PaginationPlugin)

Vue.use(PictureApiService)

export default {
  i18n,
  name: 'pictures',
  props: {
    group: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      is_loading: false,
      keyword: '',
      search_completed: false,
      pictures: [],
      total: 0,
      per_page: 30,
      page: 1,
      picture: {},
      picture_downloadable_efile: null,
      picture_printable_efile: null
    }
  },
  computed: {
    per_page_options() {
      return [
        { value: 30, text: this.$t('paginator.items-per-page-selector.option.label', { size: 30 }) },
        { value: 50, text: this.$t('paginator.items-per-page-selector.option.label', { size: 50 }) },
        { value: 100, text: this.$t('paginator.items-per-page-selector.option.label', { size: 100 }) }
      ]
    }
  },
  watch: {
    per_page(new_value, old_value) {
      this.page = 1
      this.setPicturesAndPaging()
    }
  },
  methods: {
    pageChange(page) {
      this.page = page
      this.setPicturesAndPaging()
    },
    getPicturesOrder() {
      switch (this.group) {
        case 'applicable':
          return this.$i18n.locale === 'tw'
            ? 'in_stock,asc|chinese_display_order,asc|latest_storage_date,desc|created_at,desc'
            : 'in_stock,asc|english_display_order,asc|latest_storage_date,desc|created_at,desc'
        case 'downloadable':
          return this.$i18n.locale === 'tw'
            ? 'chinese_display_order,asc|code,asc'
            : 'english_display_order,asc|code,asc'
        default:
          return 'code,asc'
      }
    },
    async setPicturesAndPaging() {
      this.is_loading = true
      this.pictures = []
      try {
        let items = {}
        let preview = { url: 'img/common.jpg' }
        
        items = await this.$pictureApiService.query(
          `filter[have_efile]&order=${this.getPicturesOrder()}&per_page=${this.per_page}&page=${this.page}`
        )
        const previewPromises = items.data.map(async (picture) => {
          picture.preview = await this.$pictureApiService.getPreviewableEfile(picture, preview)
        })
        await Promise.all(previewPromises);

        this.pictures = items.data
        this.total = items.meta.total
        this.search_completed = true
      } finally {
        this.is_loading = false
      }
    }
  },
  async created() {
    // alert(`Vue version : ${Vue.version}`)
    await this.setPicturesAndPaging()
  },
  components: {
    ApplicationListButtonToolbar,
    PictureCardList,
    PictureEfileList,
    PictureInfo
  },
  metaInfo() {
    let sub_title = ''
    switch (this.group) {
      case 'applicable':
        sub_title = this.$t('picture.list.application.title')
        break
      case 'downloadable':
        sub_title = this.$t('picture.list.download.title')
        break
      default:
        sub_title = ''
    }
    
    return {
      title: `${this.$t('picture.title')}${sub_title}`,
      meta: [{
        vmid: 'description',
        name: 'description',
        content: ''
      }]
    }
  }
}
</script>